import { memo } from "react";
import { LuHeart } from "react-icons/lu";

/**
 * Circular EatProtein Score (0–100) from the nutrition service's score
 * engine. `variant="icon"` puts a heart in the ring (cards, product screen)
 * with the score beside it; `variant="number"` puts the score inside
 * (nutrition screen). Unrated products show "Not rated", never a number.
 */
function EProteinScore({ score, size = 44, stroke = 4, variant = "icon", showText = true }) {
  const rated = score !== null && score !== undefined;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = rated ? circumference * (1 - Math.min(100, Math.max(0, score)) / 100) : circumference;
  const label = rated ? `E-Protein Score ${Math.round(score)} out of 100` : "E-Protein Score: not yet rated";

  const ring = (
    <span className="ep-ring" style={{ width: size, height: size }}>
      <svg className="ep-ring-svg" width={size} height={size} aria-hidden="true">
        <circle className="ep-ring-track" cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={stroke} />
        <circle
          className="ep-ring-value"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span className="ep-ring-center" aria-hidden="true">
        {variant === "icon" ? (
          <LuHeart style={{ fontSize: size * 0.4 }} />
        ) : (
          <>
            <b style={{ fontSize: size * 0.21 }}>
              {rated ? Math.round(score) : "–"}
              {rated && <small style={{ fontSize: size * 0.11, fontWeight: 500 }}>/100</small>}
            </b>
            <small style={{ fontSize: size * 0.085, marginTop: 2 }}>{rated ? "E-Protein Score" : "Not rated"}</small>
          </>
        )}
      </span>
    </span>
  );

  if (variant !== "icon" || !showText) {
    return (
      <span role="img" aria-label={label} style={{ display: "inline-flex" }}>
        {ring}
      </span>
    );
  }
  return (
    <span className="ep-ring-wrap" role="img" aria-label={label} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      {ring}
      <span className={`ep-score-text ${rated ? "" : "is-unrated"}`} aria-hidden="true">
        <b>
          {rated ? (
            <>
              {Math.round(score)}
              <small>/100</small>
            </>
          ) : (
            "Not rated"
          )}
        </b>
        <span>E-Protein Score</span>
      </span>
    </span>
  );
}

export default memo(EProteinScore);
